import { useState, useRef } from 'react';
import { ThemeToggle } from '@/components/ThemeToggle';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Sparkles, Target, TrendingUp, Brain, Zap, Settings, Plus, Edit, Trash2, AlertCircle, Loader2, Play, XCircle, ExternalLink } from 'lucide-react';
import { ProfileDialog } from '@/components/marketing/ProfileDialog';
import { ApiKeySettings } from '@/components/ApiKeySettings';
import { useOpenRouter, type ChatMessage } from '@/hooks/useOpenRouter';
import { useSoftwareProfiles } from '@/hooks/useSoftwareProfiles';
import { useLocalStorage } from '@/hooks/useLocalStorage';
import type { Subreddit, OpportunityPost, Strategy, ContentIdea, SoftwareProfile } from '@/types/analysis';

interface ReportResults {
  id: string;
  profileId: string;
  profileName: string;
  timestamp: number;
  subreddits: Subreddit[];
  opportunities: OpportunityPost[];
  strategies: Strategy[];
  contentIdeas: ContentIdea[];
}

export default function SimpleDashboard() {
  const { sendChatMessage, isAuthenticated, apiKey, selectedModel } = useOpenRouter();

  const {
    profiles,
    createProfile,
    updateProfile,
    deleteProfile,
  } = useSoftwareProfiles();

  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [editingProfile, setEditingProfile] = useState<SoftwareProfile | null>(null);
  const [profilesDialogOpen, setProfilesDialogOpen] = useState(false);
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [selectedProfileForRun, setSelectedProfileForRun] = useState<string>('');
  const [currentStep, setCurrentStep] = useState<string>('');

  // AbortController for canceling report generation
  const abortControllerRef = useRef<AbortController | null>(null);

  // Save reports to localStorage
  const [savedReports, setSavedReports] = useLocalStorage<ReportResults[]>('reddit-marketing:reports', []);
  const [currentReportId, setCurrentReportId] = useState<string | null>(null);

  // Get current report
  const currentReport = savedReports.find(r => r.id === currentReportId) || null;

  const handleProfileSubmit = (data: Omit<SoftwareProfile, 'id' | 'createdAt'>) => {
    if (editingProfile) {
      updateProfile(editingProfile.id, data);
      setEditingProfile(null);
    } else {
      const newProfile = createProfile(data);
      setSelectedProfileForRun(newProfile.id);
    }
  };

  const handleCreateNew = () => {
    setEditingProfile(null);
    setProfileDialogOpen(true);
  };

  const handleEditProfile = (profile: SoftwareProfile) => {
    setEditingProfile(profile);
    setProfileDialogOpen(true);
  };

  const handleDeleteProfile = (profileId: string) => {
    deleteProfile(profileId);
    if (selectedProfileForRun === profileId) {
      setSelectedProfileForRun(profiles.length > 1 ? profiles.find(p => p.id !== profileId)?.id || '' : '');
    }
  };

  const cancelReport = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setIsAnalyzing(false);
    setCurrentStep('');
    console.log('❌ Report generation cancelled by user');
  };

  const runReport = async () => {
    if (!selectedProfileForRun || !isAuthenticated) return;

    const profile = profiles.find(p => p.id === selectedProfileForRun);
    if (!profile) return;

    // Create new AbortController for this report
    abortControllerRef.current = new AbortController();
    setIsAnalyzing(true);

    console.group('🎯 Starting Report Generation');
    console.log('Profile:', profile.name);
    console.log('API Key configured:', apiKey ? 'Yes' : 'No');
    console.log('Selected Model:', selectedModel);
    console.groupEnd();

    // Create new report with unique ID
    const reportId = `report-${Date.now()}`;
    const newReport: ReportResults = {
      id: reportId,
      profileId: profile.id,
      profileName: profile.name,
      timestamp: Date.now(),
      subreddits: [],
      opportunities: [],
      strategies: [],
      contentIdeas: [],
    };

    try {
      // Step 1: Discover Subreddits
      setCurrentStep('Discovering subreddits...');
      console.log('📍 Step 1/4: Discovering subreddits...');
      newReport.subreddits = await discoverSubreddits(profile);

      // Check if cancelled
      if (abortControllerRef.current?.signal.aborted) {
        throw new Error('Cancelled');
      }
      console.log('✅ Subreddits discovered:', newReport.subreddits.length);

      // Step 2: Find Opportunities
      setCurrentStep('Finding opportunities...');
      console.log('📍 Step 2/4: Finding opportunities...');
      newReport.opportunities = await findOpportunities(profile, newReport.subreddits);

      // Check if cancelled
      if (abortControllerRef.current?.signal.aborted) {
        throw new Error('Cancelled');
      }
      console.log('✅ Opportunities found:', newReport.opportunities.length);

      // Step 3: Generate Strategies
      setCurrentStep('Generating strategies...');
      console.log('📍 Step 3/4: Generating strategies...');
      newReport.strategies = await generateStrategies(profile);

      // Check if cancelled
      if (abortControllerRef.current?.signal.aborted) {
        throw new Error('Cancelled');
      }
      console.log('✅ Strategies generated:', newReport.strategies.length);

      // Step 4: Generate Content Ideas
      setCurrentStep('Creating content ideas...');
      console.log('📍 Step 4/4: Creating content ideas...');
      newReport.contentIdeas = await generateContentIdeas(profile);

      // Check if cancelled
      if (abortControllerRef.current?.signal.aborted) {
        throw new Error('Cancelled');
      }
      console.log('✅ Content ideas created:', newReport.contentIdeas.length);

      console.log('🎉 Report generation complete!');
      console.log('Final results:', newReport);

      // Save report to history (add to beginning of array)
      setSavedReports(prev => [newReport, ...prev]);
      setCurrentReportId(reportId);
      setCurrentStep('');
    } catch (error) {
      if (error instanceof Error && error.message === 'Cancelled') {
        console.log('❌ Report generation was cancelled');
      } else {
        console.error('💥 Report generation failed:', error);
      }
      setCurrentStep('');
    } finally {
      setIsAnalyzing(false);
      abortControllerRef.current = null;
    }
  };

  const discoverSubreddits = async (prof: SoftwareProfile): Promise<Subreddit[]> => {
    const systemPrompt = `You are a Reddit marketing expert with web search capabilities. Use your web search tools to find REAL, ACTIVE subreddits where this software could be marketed.

INSTRUCTIONS:
1. Use web search to find active subreddits related to the software category and target audience
2. For each subreddit you find, verify it exists by searching for "reddit.com/r/[subreddit_name]"
3. Extract real subscriber counts and posting guidelines from the search results
4. Only include subreddits that are confirmed to exist and be active

For each REAL subreddit you find, provide:
1. Subreddit name (without r/) - must be an actual existing subreddit you found via web search
2. Description of what that subreddit is about (from search results)
3. Subscriber count (actual count from search results, format: '500K' or '1.2M')
4. Relevance score (0-100) - how relevant is this subreddit to the software
5. Reasoning for why it's relevant
6. Engagement potential (high/medium/low)
7. Key posting guidelines (from subreddit rules you find via search)

Return your response as a valid JSON array with this exact structure:
[
  {
    "name": "subreddit_name",
    "description": "what this subreddit is about",
    "subscribers": "actual count like '500K' or '1.2M'",
    "relevanceScore": 95,
    "reasoning": "why this subreddit is relevant to the software",
    "engagementPotential": "high",
    "postingGuidelines": "actual rules from the subreddit"
  }
]

Find 8-12 REAL, ACTIVE subreddits using web search. Do not make up or guess subreddit names.`;

    const userPrompt = `Software Name: ${prof.name}
Category: ${prof.category}
Description: ${prof.description}
Target Audience: ${prof.targetAudience}
Key Features: ${prof.keyFeatures}

Use web search to find the most relevant REAL, ACTIVE subreddits on Reddit for marketing this software. Search for subreddits related to: ${prof.category}, ${prof.targetAudience}.`;


    const messages: ChatMessage[] = [
      { role: 'system', content: systemPrompt },
      { role: 'user', content: userPrompt },
    ];

    console.log('🌐 Enabling web search for subreddit discovery...');
    const response = await sendChatMessage(messages, undefined, {
      plugins: [
        {
          id: 'web',
          max_results: 10,
        }
      ]
    });
    const content = response.choices[0].message.content as string;
    const jsonMatch = content.match(/\[[\s\S]*\]/);
    if (!jsonMatch) throw new Error('Could not parse subreddit data');
    const parsed = JSON.parse(jsonMatch[0]);

    console.log(`✅ Discovered ${parsed.length} subreddits from AI`);

    return parsed.sort((a: Subreddit, b: Subreddit) => b.relevanceScore - a.relevanceScore);
  };

  const findOpportunities = async (prof: SoftwareProfile, subreddits: Subreddit[]): Promise<OpportunityPost[]> => {
    const topSubreddits = subreddits.slice(0, 5);
    const subredditList = topSubreddits.map(sub => `r/${sub.name}`).join(', ');

    console.log('🔍 AI searching web for actual Reddit posts...');

    const systemPrompt = `You are a Reddit marketing expert with web search capabilities. Use your web search tools to find REAL, RECENT Reddit posts where this software could be mentioned helpfully.

CRITICAL INSTRUCTIONS - YOU MUST USE WEB SEARCH:
1. For each target subreddit, use web search with queries like:
   - "site:reddit.com/r/[subreddit] [relevant keywords from last 7 days]"
   - "reddit.com/r/[subreddit] question about [relevant topic]"
   - "reddit.com/r/[subreddit] need help with [relevant problem]"
2. Find REAL posts from the search results (recent posts within last 7-30 days)
3. Extract the EXACT post title, REAL Reddit URL, and actual post content snippet from search results
4. Only include posts where mentioning the software would be genuinely helpful and natural
5. Verify each post URL is real before including it

For each REAL opportunity you find via web search, provide:
1. Exact post title from the actual Reddit post
2. Subreddit name (without r/)
3. REAL Reddit URL (the actual URL from search results)
4. Brief snippet from actual post content (from search results)
5. Opportunity type (question/complaint/discussion/request)
6. Relevance score (0-100)
7. Suggested response approach
8. Estimated engagement potential (high/medium/low)
9. Reasoning for why this is a good opportunity
10. Key subreddit rules (from web search)

Return your response as a valid JSON array with this exact structure:
[
  {
    "title": "exact post title from Reddit",
    "subreddit": "subreddit_name",
    "url": "actual Reddit URL from search",
    "snippet": "actual excerpt from post",
    "opportunityType": "question",
    "relevanceScore": 85,
    "suggestedResponse": "how to engage helpfully",
    "estimatedEngagement": "high",
    "reasoning": "why this is a good opportunity",
    "subredditRules": "actual rules from subreddit"
  }
]

CRITICAL: You MUST use web search to find 8-15 REAL recent posts. Do not create fictional examples.`;

    const userPrompt = `Software: ${prof.name}
Category: ${prof.category}
Description: ${prof.description}
Key Features: ${prof.keyFeatures}
Target Audience: ${prof.targetAudience}

Use web search to find REAL recent posts in these subreddits: ${subredditList}

Search for posts where users are:
- Asking questions about ${prof.category}
- Complaining about problems related to ${prof.description}
- Discussing challenges that ${prof.name} could solve
- Requesting tool recommendations for ${prof.targetAudience}

Return actual posts you find via web search with real URLs and real content.`;

    const messages: ChatMessage[] = [
      { role: 'system', content: systemPrompt },
      { role: 'user', content: userPrompt },
    ];

    console.log('🌐 Enabling web search for finding post opportunities...');
    const response = await sendChatMessage(messages, undefined, {
      plugins: [
        {
          id: 'web',
          max_results: 15,
        }
      ]
    });
    const content = response.choices[0].message.content as string;
    const jsonMatch = content.match(/\[[\s\S]*\]/);
    if (!jsonMatch) throw new Error('Could not parse opportunity data');
    const parsed = JSON.parse(jsonMatch[0]);
    return parsed.sort((a: OpportunityPost, b: OpportunityPost) => b.relevanceScore - a.relevanceScore);
  };

  const generateStrategies = async (prof: SoftwareProfile): Promise<Strategy[]> => {
    const systemPrompt = `You are a Reddit marketing strategist. Create comprehensive marketing strategies for software products.

For each strategy, provide:
1. Title
2. Description
3. Priority (high/medium/low)
4. Effort required (low/medium/high)
5. Expected impact description
6. Step-by-step implementation steps
7. Real examples
8. Best practices to follow

Return as valid JSON array:
[
  {
    "title": "strategy name",
    "description": "what it is",
    "priority": "high",
    "effort": "medium",
    "expectedImpact": "what to expect",
    "steps": ["step 1", "step 2"],
    "examples": ["example 1", "example 2"],
    "bestPractices": ["practice 1", "practice 2"]
  }
]

Provide 5-7 diverse strategies covering different approaches.`;

    const userPrompt = `Software: ${prof.name}
Category: ${prof.category}
Description: ${prof.description}
Target Audience: ${prof.targetAudience}
Key Features: ${prof.keyFeatures}

Create Reddit marketing strategies for this software.`;

    const messages: ChatMessage[] = [
      { role: 'system', content: systemPrompt },
      { role: 'user', content: userPrompt },
    ];

    const response = await sendChatMessage(messages);
    const content = response.choices[0].message.content as string;
    const jsonMatch = content.match(/\[[\s\S]*\]/);
    if (!jsonMatch) throw new Error('Could not parse strategy data');
    return JSON.parse(jsonMatch[0]);
  };

  const generateContentIdeas = async (prof: SoftwareProfile): Promise<ContentIdea[]> => {
    const systemPrompt = `You are a Reddit content strategist. Create engaging post ideas for software marketing.

For each content idea, provide:
1. Title/headline
2. Type (guide/showcase/question/discussion/story)
3. Suggested subreddits (array)
4. Content outline (array of bullet points)
5. Estimated engagement (high/medium/low)
6. Best timing/when to post

Return as valid JSON array:
[
  {
    "title": "post title",
    "type": "guide",
    "suggestedSubreddits": ["subreddit1", "subreddit2"],
    "outline": ["point 1", "point 2"],
    "estimatedEngagement": "high",
    "timing": "when to post"
  }
]

Provide 8-10 diverse content ideas.`;

    const userPrompt = `Software: ${prof.name}
Category: ${prof.category}
Description: ${prof.description}
Target Audience: ${prof.targetAudience}

Create engaging Reddit post ideas for this software.`;

    const messages: ChatMessage[] = [
      { role: 'system', content: systemPrompt },
      { role: 'user', content: userPrompt },
    ];

    const response = await sendChatMessage(messages);
    const content = response.choices[0].message.content as string;
    const jsonMatch = content.match(/\[[\s\S]*\]/);
    if (!jsonMatch) throw new Error('Could not parse content ideas');
    return JSON.parse(jsonMatch[0]);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-50 via-purple-50/50 to-fuchsia-50/30 dark:from-gray-950 dark:via-violet-950/20 dark:to-purple-950/10">
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-violet-400/20 dark:bg-violet-600/10 rounded-full blur-3xl" />
        <div className="absolute top-1/2 -left-40 w-96 h-96 bg-fuchsia-400/15 dark:bg-fuchsia-600/5 rounded-full blur-3xl" />
      </div>

      <div className="relative container mx-auto px-4 py-6 max-w-6xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-2xl bg-gradient-to-br from-violet-600 to-fuchsia-600 shadow-lg shadow-violet-500/25">
              <Zap className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold bg-gradient-to-r from-violet-600 to-fuchsia-600 bg-clip-text text-transparent">
                Reddit Prospector
              </h1>
              <p className="text-xs text-muted-foreground">Smart automation for growth</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSettingsDialogOpen(true)}
              className="h-9 w-9 rounded-lg"
            >
              <Settings className="h-[1.2rem] w-[1.2rem]" />
              <span className="sr-only">Open settings</span>
            </Button>
            <ThemeToggle />
          </div>
        </div>

        {/* Profile Dialog */}
        <ProfileDialog
          open={profileDialogOpen}
          onOpenChange={setProfileDialogOpen}
          onSubmit={handleProfileSubmit}
          editingProfile={editingProfile}
        />

        {/* Profiles Management Dialog */}
        <Dialog open={profilesDialogOpen} onOpenChange={setProfilesDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Manage Software Profiles</DialogTitle>
              <DialogDescription>
                Create and manage profiles for different software products
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 mt-4">
              <Button
                onClick={handleCreateNew}
                className="w-full bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700 text-white shadow-lg shadow-violet-500/25 rounded-xl h-10 text-sm font-medium"
              >
                <Plus className="h-4 w-4 mr-2" />
                Create New Profile
              </Button>

              {profiles.length === 0 ? (
                <Card className="border-dashed">
                  <CardContent className="py-8 text-center">
                    <p className="text-sm text-muted-foreground">No profiles yet. Create your first one!</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-3">
                  {profiles.map((profile) => (
                    <Card key={profile.id} className="overflow-hidden">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0 space-y-1">
                            <h3 className="text-sm font-bold truncate">{profile.name}</h3>
                            <Badge className="bg-violet-100 dark:bg-violet-900/40 text-violet-700 dark:text-violet-300 border-0 rounded-full text-xs">
                              {profile.category}
                            </Badge>
                            <p className="text-xs text-muted-foreground line-clamp-2">{profile.description}</p>
                          </div>
                          <div className="flex gap-2 shrink-0">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0 rounded-lg"
                              onClick={() => handleEditProfile(profile)}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0 rounded-lg hover:text-destructive"
                              onClick={() => handleDeleteProfile(profile.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Settings Dialog */}
        <Dialog open={settingsDialogOpen} onOpenChange={setSettingsDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>API Settings</DialogTitle>
              <DialogDescription>
                Configure your OpenRouter API key to enable AI analysis
              </DialogDescription>
            </DialogHeader>
            <div className="mt-4">
              <ApiKeySettings />
            </div>
          </DialogContent>
        </Dialog>

        {/* Run Report Card */}
        <Card className="glass-card rounded-3xl overflow-hidden border-2 border-violet-200/50 dark:border-violet-500/20 mb-6">
          <CardHeader className="pb-4">
            <div className="flex items-start justify-between">
              <div className="space-y-1">
                <CardTitle className="text-2xl font-bold">Generate Marketing Report</CardTitle>
                <CardDescription className="text-sm">
                  Select a software profile and run a comprehensive analysis
                </CardDescription>
              </div>
              <div className="p-3 rounded-2xl bg-gradient-to-br from-violet-500/10 to-fuchsia-500/10">
                <Sparkles className="h-6 w-6 text-violet-600 dark:text-violet-400" />
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {!isAuthenticated && (
              <Alert className="rounded-2xl border-amber-200 dark:border-amber-500/20 bg-amber-50/50 dark:bg-amber-950/20">
                <AlertCircle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                <AlertDescription className="text-sm text-amber-700 dark:text-amber-300">
                  Please configure your OpenRouter API key to start analyzing.{' '}
                  <button
                    onClick={() => setSettingsDialogOpen(true)}
                    className="underline font-medium hover:text-amber-800 dark:hover:text-amber-200"
                  >
                    Configure now
                  </button>
                </AlertDescription>
              </Alert>
            )}

            <div className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Select Software Profile</label>
                {profiles.length === 0 ? (
                  <Button
                    onClick={() => setProfilesDialogOpen(true)}
                    variant="outline"
                    className="w-full h-12 rounded-xl border-dashed border-2 hover:bg-violet-50 dark:hover:bg-violet-950/30"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Create Your First Profile
                  </Button>
                ) : (
                  <Select value={selectedProfileForRun} onValueChange={setSelectedProfileForRun}>
                    <SelectTrigger className="w-full h-12 rounded-xl">
                      <SelectValue placeholder="Choose a profile..." />
                    </SelectTrigger>
                    <SelectContent>
                      {profiles.map((profile) => (
                        <SelectItem key={profile.id} value={profile.id}>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{profile.name}</span>
                            <span className="text-xs text-muted-foreground">({profile.category})</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={() => setProfilesDialogOpen(true)}
                  variant="outline"
                  className="flex-1 rounded-xl border-violet-200/50 dark:border-violet-500/20 hover:bg-violet-50 dark:hover:bg-violet-950/30 h-12 text-sm font-medium"
                  disabled={isAnalyzing}
                >
                  <Settings className="h-4 w-4 mr-2" />
                  Manage Profiles
                </Button>
                {isAnalyzing ? (
                  <Button
                    onClick={cancelReport}
                    variant="destructive"
                    className="flex-1 rounded-xl h-12 text-sm font-medium"
                  >
                    <XCircle className="h-4 w-4 mr-2" />
                    Cancel
                  </Button>
                ) : (
                  <Button
                    onClick={runReport}
                    disabled={!selectedProfileForRun || !isAuthenticated}
                    className="flex-1 bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700 text-white shadow-lg shadow-violet-500/25 rounded-xl h-12 text-sm font-medium disabled:opacity-50"
                  >
                    <Play className="h-4 w-4 mr-2" />
                    Run Report
                  </Button>
                )}
              </div>

              {isAnalyzing && currentStep && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>{currentStep}</span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Saved Reports History */}
        {savedReports.length > 0 && (
          <Card className="glass-card rounded-3xl overflow-hidden mb-6">
            <CardHeader>
              <CardTitle className="text-xl font-bold">Saved Reports</CardTitle>
              <CardDescription>View and manage your past analyses</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {savedReports.map((report) => (
                <Card
                  key={report.id}
                  className={`overflow-hidden cursor-pointer transition-all ${
                    currentReportId === report.id ? 'ring-2 ring-violet-500' : 'hover:shadow-lg'
                  }`}
                  onClick={() => setCurrentReportId(report.id)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0 space-y-2">
                        <div className="flex items-center gap-2">
                          <h3 className="text-sm font-semibold truncate">{report.profileName}</h3>
                          {currentReportId === report.id && (
                            <Badge className="bg-violet-500/10 text-violet-600 border-violet-500/20 rounded-full text-xs">
                              Viewing
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {new Date(report.timestamp).toLocaleString()}
                        </p>
                        <div className="flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
                          <div className="flex items-center gap-1">
                            <Target className="h-3 w-3" />
                            {report.subreddits.length}
                          </div>
                          <div className="flex items-center gap-1">
                            <TrendingUp className="h-3 w-3" />
                            {report.opportunities.length}
                          </div>
                          <div className="flex items-center gap-1">
                            <Brain className="h-3 w-3" />
                            {report.strategies.length}
                          </div>
                          <div className="flex items-center gap-1">
                            <Sparkles className="h-3 w-3" />
                            {report.contentIdeas.length}
                          </div>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="shrink-0 h-8 w-8 p-0 rounded-lg text-muted-foreground hover:text-destructive"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSavedReports(prev => prev.filter(r => r.id !== report.id));
                          if (currentReportId === report.id) {
                            setCurrentReportId(null);
                          }
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Results */}
        {currentReport && (
          <Card className="glass-card rounded-3xl overflow-hidden">
            <CardHeader>
              <CardTitle className="text-2xl font-bold">{currentReport.profileName} - Marketing Report</CardTitle>
              <CardDescription>
                Generated {new Date(currentReport.timestamp).toLocaleString()}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="subreddits" className="space-y-5">
                <TabsList className="grid w-full grid-cols-3 h-auto p-1.5 glass-card rounded-2xl">
                  <TabsTrigger
                    value="subreddits"
                    className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-violet-600 data-[state=active]:to-fuchsia-600 data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:shadow-violet-500/25 rounded-xl py-2.5 text-xs font-medium transition-all"
                  >
                    <Target className="h-3.5 w-3.5 mr-1.5" />
                    Subreddits ({currentReport.subreddits.length})
                  </TabsTrigger>
                  <TabsTrigger
                    value="opportunities"
                    className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-violet-600 data-[state=active]:to-fuchsia-600 data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:shadow-violet-500/25 rounded-xl py-2.5 text-xs font-medium transition-all"
                  >
                    <TrendingUp className="h-3.5 w-3.5 mr-1.5" />
                    Live Posts ({currentReport.opportunities.length})
                  </TabsTrigger>
                  <TabsTrigger
                    value="strategies"
                    className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-violet-600 data-[state=active]:to-fuchsia-600 data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:shadow-violet-500/25 rounded-xl py-2.5 text-xs font-medium transition-all"
                  >
                    <Brain className="h-3.5 w-3.5 mr-1.5" />
                    Strategies ({currentReport.strategies.length + currentReport.contentIdeas.length})
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="subreddits" className="space-y-3">
                  {currentReport.subreddits.map((sub, index) => (
                    <Card key={index}>
                      <CardContent className="p-4">
                        <div className="space-y-2">
                          <div className="flex items-start justify-between">
                            <h3 className="font-bold">r/{sub.name}</h3>
                            <Badge className="bg-violet-100 dark:bg-violet-900/40 text-violet-700 dark:text-violet-300 border-0 rounded-full text-xs">
                              {sub.relevanceScore}/100
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground">{sub.description}</p>
                          <div className="flex gap-2 text-xs">
                            <Badge variant="outline">{sub.subscribers} subscribers</Badge>
                            <Badge variant="outline">{sub.engagementPotential} engagement</Badge>
                          </div>
                          <p className="text-xs"><strong>Guidelines:</strong> {sub.postingGuidelines}</p>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </TabsContent>

                <TabsContent value="opportunities" className="space-y-3">
                  {currentReport.opportunities.length === 0 ? (
                    <Card className="border-dashed">
                      <CardContent className="py-8 text-center">
                        <p className="text-sm text-muted-foreground">
                          No relevant opportunities found in recent posts. Try running the report again later when there are new posts.
                        </p>
                      </CardContent>
                    </Card>
                  ) : (
                    currentReport.opportunities.map((opp, index) => (
                      <Card key={index}>
                        <CardContent className="p-4">
                          <div className="space-y-2">
                            <div className="flex items-start justify-between gap-2">
                              <a
                                href={opp.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="font-bold text-sm hover:text-violet-600 dark:hover:text-violet-400 transition-colors flex-1"
                              >
                                {opp.title}
                              </a>
                              <Badge className="bg-violet-100 dark:bg-violet-900/40 text-violet-700 dark:text-violet-300 border-0 rounded-full text-xs shrink-0">
                                {opp.relevanceScore}/100
                              </Badge>
                            </div>
                            <a
                              href={`https://reddit.com/r/${opp.subreddit}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-muted-foreground hover:text-violet-600 dark:hover:text-violet-400 transition-colors inline-block"
                            >
                              r/{opp.subreddit}
                            </a>
                            <p className="text-sm">{opp.snippet}</p>
                            <div className="flex gap-2 text-xs flex-wrap">
                              <Badge variant="outline">{opp.opportunityType}</Badge>
                              <Badge variant="outline">{opp.estimatedEngagement} engagement</Badge>
                            </div>
                            <div className="pt-2 border-t border-border/50 space-y-1">
                              <p className="text-xs"><strong>Why this is relevant:</strong> {opp.reasoning}</p>
                              <p className="text-xs"><strong>Suggested approach:</strong> {opp.suggestedResponse}</p>
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              className="w-full mt-2 rounded-lg"
                              onClick={() => window.open(opp.url, '_blank')}
                            >
                              <ExternalLink className="h-3.5 w-3.5 mr-2" />
                              View Post on Reddit
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </TabsContent>

                <TabsContent value="strategies" className="space-y-3">
                  {currentReport.strategies.map((strat, index) => (
                    <Card key={`strat-${index}`}>
                      <CardContent className="p-4">
                        <div className="space-y-2">
                          <h3 className="font-bold">{strat.title}</h3>
                          <p className="text-sm">{strat.description}</p>
                          <div className="flex gap-2 text-xs">
                            <Badge variant="outline">{strat.priority} priority</Badge>
                            <Badge variant="outline">{strat.effort} effort</Badge>
                          </div>
                          <p className="text-xs"><strong>Expected Impact:</strong> {strat.expectedImpact}</p>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                  {currentReport.contentIdeas.map((idea, index) => (
                    <Card key={`content-${index}`}>
                      <CardContent className="p-4">
                        <div className="space-y-2">
                          <h3 className="font-bold">{idea.title}</h3>
                          <div className="flex gap-2 text-xs">
                            <Badge variant="outline">{idea.type}</Badge>
                            <Badge variant="outline">{idea.estimatedEngagement} engagement</Badge>
                          </div>
                          <p className="text-xs"><strong>Subreddits:</strong> {idea.suggestedSubreddits.join(', ')}</p>
                          <p className="text-xs"><strong>Timing:</strong> {idea.timing}</p>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
